import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MultiSelectFilter } from "@/components/ui/multi-select-filter";
import { TEXTAREA_CLS } from "@/lib/analysis";
import { type AnalysisStreamFailure, streamAnalysis } from "@/lib/analysis-stream";
import { api } from "@/lib/api";
import { formatNumber } from "@/lib/i18n";
import type { TFunction } from "i18next";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/** The same failures, worded for a pasted transcript. */
function analyzeError(f: AnalysisStreamFailure, t: TFunction): string {
  switch (f.reason) {
    case "http":
      if (f.status === 503) {
        return f.detail ?? t("analysisTools.transcript.errors.noAi");
      }
      if (f.status === 400) return f.detail ?? t("analysisTools.transcript.errors.empty");
      return f.detail
        ? t("analysisTools.transcript.errors.failedDetail", { detail: f.detail })
        : t("analysisTools.transcript.errors.failed");
    case "stream":
      return t("analysisTools.transcript.errors.stream", {
        detail: f.detail ?? t("analysisTools.transcript.errors.unknown"),
      });
    case "incomplete":
    case "interrupted":
      return t("analysisTools.transcript.errors.lost");
    case "timeout":
      return t("analysisTools.transcript.errors.timeout");
    default:
      return t("analysisTools.transcript.errors.noResponse");
  }
}

/**
 * The one **manually-run** analysis source: a pasted meeting transcript.
 * Lives at the top of the AI Suggestions page (collapsed by default). Running
 * it saves the proposals as pending suggestions; the parent then refreshes
 * the queue below, where the admin approves/rejects them — so there are no
 * separate inline cards here, just the run + a short confirmation.
 */

type AnalyzeResult = {
  analysisId: string | null;
  summary: string;
  suggestions: Array<{ id: string }>;
  /** Proposed time entries — a separate entity with its own queue, but part of
   *  the same run, so the "added N proposals" count has to include them. */
  timeSuggestions: Array<{ id: string }>;
};

export function TranscriptAnalyzer({
  onAnalyzed,
  defaultOpen = false,
}: {
  onAnalyzed: () => void;
  /** Skip the collapsed "+ Analyse…" button and show the form straight away
   *  (e.g. when rendered inside a modal). */
  defaultOpen?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  const [projects, setProjects] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
  const [transcript, setTranscript] = useState("");

  const [analyzing, setAnalyzing] = useState(false);
  const [progress, setProgress] = useState<{ tokens: number; phase: "thinking" | "writing" }>({
    tokens: 0,
    phase: "thinking",
  });
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ summary: string; count: number } | null>(null);

  useEffect(() => {
    if (!open || projects.length) return;
    api
      .get<{ projects: Array<{ id: string; name: string }> }>("/api/projects?pageSize=200")
      .then((res) => setProjects(res.projects.map((p) => ({ id: p.id, name: p.name }))))
      .catch(() => {
        /* the picker just stays empty; analysis can still run with no project */
      });
  }, [open, projects.length]);

  async function analyze() {
    setError(null);
    setDone(null);
    const text = transcript.trim();
    if (!text) {
      setError(t("analysisTools.transcript.pasteFirst"));
      return;
    }

    setAnalyzing(true);
    setProgress({ tokens: 0, phase: "thinking" });

    try {
      const run = await streamAnalysis<AnalyzeResult>(
        "/api/analysis/analyze",
        { transcript: text, projectIds: selectedProjectIds },
        { onProgress: setProgress },
      );

      if (!run.ok) {
        setError(analyzeError(run, t));
        return;
      }
      const final = run.result;
      setDone({
        summary: final.summary,
        count: final.suggestions.length + (final.timeSuggestions?.length ?? 0),
      });
      setTranscript("");
      onAnalyzed();
    } finally {
      setAnalyzing(false);
    }
  }

  if (!open) {
    return (
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen(true)}
        data-testid="open-transcript-analyzer"
      >
        {t("analysisTools.transcript.open")}
      </Button>
    );
  }

  const projectOptions = projects.map((p) => ({ value: p.id, label: p.name }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("analysisTools.transcript.title")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <span className="text-sm font-medium leading-none">
            {t("analysisTools.transcript.projects")}
          </span>
          <MultiSelectFilter
            options={projectOptions}
            values={selectedProjectIds}
            exclude={false}
            onChange={setSelectedProjectIds}
            placeholder={t("analysisTools.transcript.projectsPlaceholder")}
            noun={t("analysisTools.transcript.projectsNoun")}
            aria-label={t("analysisTools.transcript.projects")}
          />
          <p className="text-xs text-muted-foreground">
            {t("analysisTools.transcript.projectsHelp")}
          </p>
        </div>

        <div className="space-y-2">
          <label htmlFor="transcript" className="text-sm font-medium leading-none">
            {t("analysisTools.transcript.text")}
          </label>
          <textarea
            id="transcript"
            data-testid="transcript-input"
            value={transcript}
            onChange={(e) => setTranscript(e.target.value)}
            rows={10}
            placeholder={t("analysisTools.transcript.placeholder")}
            className={TEXTAREA_CLS}
          />
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}
        {done && (
          <div className="rounded-md bg-muted/50 p-3 text-sm" data-testid="analyze-done">
            <p className="font-medium">
              {t("analysisTools.transcript.added", { count: done.count })}
            </p>
            {done.summary && <p className="mt-1 text-muted-foreground">{done.summary}</p>}
          </div>
        )}

        <div className="space-y-3">
          <Button type="button" onClick={analyze} disabled={analyzing} data-testid="analyze-button">
            {analyzing ? t("analysisTools.transcript.running") : t("analysisTools.transcript.run")}
          </Button>
          {analyzing && <AnalyzeProgress tokens={progress.tokens} phase={progress.phase} />}
        </div>
      </CardContent>
    </Card>
  );
}

/** Soft target (output tokens) a typical analysis produces — turns the real
 *  streamed token count into a percentage, capped at 95% until the result lands. */
const PROGRESS_TOKEN_TARGET = 3000;

function AnalyzeProgress({ tokens, phase }: { tokens: number; phase: "thinking" | "writing" }) {
  const { t } = useTranslation();
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const pct =
    phase === "thinking"
      ? 6
      : Math.min(95, Math.max(8, Math.round((tokens / PROGRESS_TOKEN_TARGET) * 100)));
  const mmss = `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, "0")}`;

  return (
    <output className="block space-y-1" data-testid="analyze-progress" aria-live="polite">
      <div
        className="h-2 w-full overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={t("analysisTools.transcript.progressLabel")}
        tabIndex={-1}
      >
        <div
          className={`h-full rounded-full bg-primary transition-[width] duration-300 ease-out ${
            phase === "thinking" ? "animate-pulse" : ""
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="text-xs text-muted-foreground">
        {phase === "thinking"
          ? t("analysisTools.transcript.reading")
          : t("analysisTools.transcript.writing")}
        {phase === "writing" && (
          <span data-testid="analyze-tokens">
            {" · "}
            {t("analysisTools.transcript.tokens", { value: formatNumber(tokens, undefined, {}) })}
          </span>
        )}
        {" · "}
        {mmss}
      </p>
    </output>
  );
}
